/**
 * The "plan my migration" form, extracted so /pilot can embed it
 * inline (no 3-click hop) and /pilot/apply can keep using it as a
 * standalone page.
 *
 * The component + endpoint keep the "pilot" name because the route
 * (/pilot/apply) and server endpoint (/public/pilot-applications) still
 * use it; the launch pilot offer itself closed 2026-11-01 and this is
 * now a migration-planning lead form. Submit posts to
 * /public/pilot-applications which creates an unverified Operator row +
 * emails info@nestplot.com. See
 * routes/public_intake.py:submit_pilot_application for the
 * server-side flow.
 */
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { trackConversion } from "../../lib/analytics";
import { intake } from "../../lib/api";
import { withUtm } from "../../lib/utm";
import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Input, Textarea } from "../../lib/Input";

// The 5 first-class verticals the operator can pick. "mixed" is no
// longer a clickable chip — it's derived from the selection (added
// automatically to the submitted payload when 2+ verticals are
// picked, so ops triage can filter on a single "mixed-portfolio"
// flag without inspecting every combination).
const VERTICALS = [
  { value: "multifamily", label: "Multifamily" },
  { value: "self_storage", label: "Self-storage" },
  { value: "parking", label: "Parking" },
  { value: "marina", label: "Marina" },
  { value: "vehicle_storage", label: "RV / vehicle storage" },
] as const;

type Vertical = (typeof VERTICALS)[number]["value"];

// Per-vertical noun for the "units" field — operators of each
// vertical actually count different things, and the form should ask
// in their language. Multi-select uses the catch-all noun ("doors /
// units / spots / slips") so the operator can sum across whatever
// they're running.
const UNITS_NOUN: Record<Vertical, string> = {
  multifamily: "apartments",
  self_storage: "units",
  parking: "stalls",
  marina: "slips",
  vehicle_storage: "bays",
};

interface FormState {
  name: string;
  email: string;
  company: string;
  phone: string;
  // Operators with mixed portfolios (apartments + storage on the same
  // parcel, etc.) need to pick more than one. Order is preserved.
  verticals: Vertical[];
  num_properties: string;
  num_units: string;
  current_system: string;
  notes: string;
}

const INITIAL: FormState = {
  name: "",
  email: "",
  company: "",
  phone: "",
  verticals: [],
  num_properties: "",
  num_units: "",
  current_system: "",
  notes: "",
};

interface PilotApplicationFormProps {
  /** Used by /pilot to anchor-jump from the "Apply to qualify" CTAs.
   * /pilot/apply doesn't need an anchor — it's the whole page. */
  anchorId?: string;
  /** Pre-selected vertical chips. Used by /pilot/{vertical} so a
   * prospect landing from a vertical-specific ad sees their vertical
   * already picked, no extra click. Empty by default. */
  initialVerticals?: readonly Vertical[];
}

export function PilotApplicationForm({
  anchorId,
  initialVerticals,
}: PilotApplicationFormProps) {
  const [form, setForm] = useState<FormState>(() =>
    initialVerticals && initialVerticals.length > 0
      ? { ...INITIAL, verticals: [...initialVerticals] }
      : INITIAL,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleVertical(v: Vertical) {
    setForm((f) => ({
      ...f,
      verticals: f.verticals.includes(v)
        ? f.verticals.filter((x) => x !== v)
        : [...f.verticals, v],
    }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (form.verticals.length === 0) {
      setError("Please pick at least one vertical.");
      return;
    }
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }

    setSubmitting(true);
    try {
      // Auto-append "mixed" when more than one first-class vertical is
      // picked so ops can filter mixed-portfolio applications without
      // checking every combination. The operator no longer picks
      // "Mixed verticals" themselves — it's derived from the selection.
      const verticalsPayload =
        form.verticals.length > 1
          ? [...form.verticals, "mixed" as const]
          : form.verticals;
      // One conversion id, shared between the request body (→ server-
      // side Meta CAPI Lead event) and the browser pixel below.
      const eventId = crypto.randomUUID();
      await intake.submitPilotApplication(withUtm({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || undefined,
        phone: form.phone.trim() || undefined,
        verticals: verticalsPayload,
        num_properties: form.num_properties
          ? Number(form.num_properties)
          : undefined,
        num_units: form.num_units ? Number(form.num_units) : undefined,
        current_system: form.current_system.trim() || undefined,
        notes: form.notes.trim() || undefined,
        event_id: eventId,
      }));
      // GTM conversion event — fired only after the server-side
      // submission succeeded so we don't attribute failed validation
      // attempts as conversions.
      trackConversion("pilot_application_submitted", {
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        vertical:
          form.verticals.length === 1 ? form.verticals[0] : "mixed",
        event_id: eventId,
      });
      setSubmitted(true);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Submission failed — try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Card>
        <div id={anchorId} className="px-8 py-10 text-center space-y-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-accent grid place-items-center text-on-accent">
            <Icon name="check" size={28} />
          </div>
          <h2 className="font-display font-black text-[26px] tracking-[-0.02em]">
            Thanks — we'll be in touch.
          </h2>
          <p className="text-sub leading-relaxed max-w-[460px] mx-auto">
            Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}. We'll reply
            to <strong>{form.email}</strong> within 1 business day with a
            migration plan — or a quick clarifying question.
          </p>
          <div className="flex flex-wrap gap-3 justify-center pt-2">
            <Link to="/">
              <Btn kind="secondary">Back to NestPlot</Btn>
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  // Labels adjust to the operator's vertical mix.
  const singleVertical =
    form.verticals.length === 1 ? form.verticals[0] : null;
  const multiVertical = form.verticals.length > 1;
  const unitsLabel = singleVertical
    ? `Total ${UNITS_NOUN[singleVertical]} across all properties`
    : multiVertical
      ? "Total doors / units / spots / slips across every property"
      : "Total units across all properties";
  const propertiesLabel = multiVertical
    ? "Total properties across all verticals"
    : "Number of properties";
  const currentSystemPlaceholder = multiVertical
    ? "e.g. AppFolio for apartments, storEDGE for storage, spreadsheets for the lot"
    : "e.g. storEDGE, AppFolio, Yardi Breeze, spreadsheets, paper";

  return (
    <Card>
      <form
        id={anchorId}
        className="px-6 py-6 lg:px-8 lg:py-8 space-y-6"
        onSubmit={onSubmit}
      >
        <section className="space-y-4">
          <div className="font-mono text-[11px] text-sub uppercase tracking-[0.14em]">
            About you
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label="Your name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              required
              autoComplete="name"
              placeholder="Casey Rivera"
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              required
              autoComplete="email"
              placeholder="casey@yourproperty.com"
            />
            <Input
              label="Company"
              value={form.company}
              onChange={(e) => update("company", e.target.value)}
              autoComplete="organization"
              placeholder="Rivera Property Group"
            />
            <Input
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              autoComplete="tel"
              placeholder="(555) 555-0100"
            />
          </div>
        </section>

        <section className="space-y-4 border-t border-line pt-6">
          <div className="font-mono text-[11px] text-sub uppercase tracking-[0.14em]">
            About your properties
          </div>
          <div>
            <label className="block font-mono text-[11px] text-sub uppercase tracking-[0.08em] mb-2">
              Vertical(s) *{" "}
              <span className="opacity-70">— pick all that apply</span>
            </label>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {VERTICALS.map((v) => {
                const active = form.verticals.includes(v.value);
                return (
                  <button
                    key={v.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleVertical(v.value)}
                    className={
                      "border-[1.5px] rounded-md px-3 py-2 text-[13.5px] font-semibold transition-colors text-left flex items-center gap-2 " +
                      (active
                        ? "border-ink bg-ink text-paper"
                        : "border-line-strong bg-paper hover:border-accent")
                    }
                  >
                    <span
                      className={
                        "inline-flex items-center justify-center w-4 h-4 rounded-sm border-[1.5px] shrink-0 " +
                        (active
                          ? "bg-paper border-paper text-ink"
                          : "border-line-strong")
                      }
                      aria-hidden
                    >
                      {active ? <Icon name="check" size={12} /> : null}
                    </span>
                    {v.label}
                  </button>
                );
              })}
            </div>
            {form.verticals.length > 1 && (
              <div className="mt-2 text-[12px] text-sub flex items-center gap-1.5">
                <Icon name="info" size={12} />
                <span>
                  We'll tag this as a <strong>mixed-vertical</strong>{" "}
                  application for triage.
                </span>
              </div>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label={propertiesLabel}
              type="number"
              min={1}
              value={form.num_properties}
              onChange={(e) => update("num_properties", e.target.value)}
              placeholder="3"
            />
            <Input
              label={unitsLabel}
              type="number"
              min={1}
              value={form.num_units}
              onChange={(e) => update("num_units", e.target.value)}
              placeholder="120"
            />
          </div>
          <Input
            label="What software are you using today?"
            value={form.current_system}
            onChange={(e) => update("current_system", e.target.value)}
            placeholder={currentSystemPlaceholder}
          />
        </section>

        <section className="space-y-4 border-t border-line pt-6">
          <div className="font-mono text-[11px] text-sub uppercase tracking-[0.14em]">
            Anything else?
          </div>
          <Textarea
            label="What's prompting the switch? Pain points, deadlines, integrations you need"
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={4}
            maxLength={10_000}
            placeholder="Optional — two sentences is plenty."
          />
        </section>

        {error && (
          <div className="bg-alert-soft border border-alert/40 text-alert px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="border-t border-line pt-6 flex flex-wrap gap-3 items-center justify-between">
          <p className="text-sub text-[12.5px] max-w-[420px]">
            By submitting, you create a NestPlot account so we can send you a
            one-click link to start your 30-day trial and plan your migration.
          </p>
          <Btn
            type="submit"
            kind="primary"
            size="lg"
            iconRight={<Icon name="arrow-right" size={14} />}
            disabled={submitting}
          >
            {submitting ? "Sending…" : "Plan my migration"}
          </Btn>
        </div>
      </form>
    </Card>
  );
}
